import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, MapPin, Clock } from "lucide-react";
import { CITIES, CANTONS, cityImage } from "../data/cities";
import { CHILD_SEATS } from "../data/pricing";
import { GUIDES, guideField } from "../data/guides";
import { TOPICS } from "../lib/blogContent";
import { useLang } from "../i18n/LanguageContext";
import BookingWidget from "../components/BookingWidget";

const Ratgeber = () => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return CITIES;
    return CITIES.filter((c) => c.name.toLowerCase().includes(s) || c.cantonName.toLowerCase().includes(s));
  }, [q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((c) => { (g[c.canton] = g[c.canton] || []).push(c); });
    return Object.keys(g).sort((a, b) => (CANTONS[a] || a).localeCompare(CANTONS[b] || b)).map((k) => ({ canton: CANTONS[k] || k, cities: g[k] }));
  }, [filtered]);

  return (
    <div className="pt-[68px] min-h-screen bg-white">
      <div className="bg-[#0B1533] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="font-body text-sm text-[#2B4BFF] font-semibold mb-3">{t("blog.kicker")}</p>
          <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-5xl leading-[1.05] max-w-2xl">{t("blog.title")}</h1>
          <div className="relative mt-8 max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA0B0]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("blog.search")} className="w-full bg-white rounded-full pl-11 pr-4 py-3.5 font-body text-sm text-[#0B1533] focus:outline-none focus:ring-2 focus:ring-[#2B4BFF]" />
          </div>
          <p className="font-body text-sm text-white/50 mt-4">{filtered.length} Ziele</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        <BookingWidget />
      </div>

      {/* Travel guides */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-b border-[#E9E5DE]" data-testid="guides-section">
        <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">Reiseratgeber</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {GUIDES.map((g) => (
            <Link key={g.slug} to={`/ratgeber/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group flex flex-col rounded-2xl border border-[#E9E5DE] bg-white overflow-hidden hover:border-[#2B4BFF] hover:elev-soft transition-all">
              <div className="h-36 overflow-hidden">
                <img src={g.image} alt={guideField(g, "h1", lang)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <span className="font-body text-xs font-semibold text-[#2B4BFF] bg-[#EEF1FF] rounded-full px-3 py-1 self-start">{guideField(g, "tag", lang)}</span>
                <h3 className="font-heading font-semibold text-[#0B1533] leading-snug mt-3 flex-1">{guideField(g, "h1", lang)}</h3>
                <span className="inline-flex items-center gap-1 font-body text-sm text-[#2B4BFF] mt-3">Weiterlesen <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Featured keyword topics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-b border-[#E9E5DE]">
        <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">Beliebte Themen</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {TOPICS.map((tp) => (
            <Link key={tp.slug} to={`/${tp.slug}`} className="group flex items-center justify-between rounded-2xl border border-[#E9E5DE] bg-white px-5 py-4 hover:border-[#2B4BFF] transition-colors">
              <span className="font-heading font-semibold text-[#0B1533]">{tp.h1}</span>
              <ArrowRight size={16} className="text-[#2B4BFF] group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </div>

      {/* Child seat guides */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-b border-[#E9E5DE]">
        <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-5">Kindersitze</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {CHILD_SEATS.map((s) => (
            <Link key={s.id} to={`/kindersitze/${s.slug}`} className="group flex items-center gap-4 rounded-2xl border border-[#E9E5DE] p-4 hover:border-[#2B4BFF] transition-colors">
              <img src={s.img} alt={s.name} className="w-16 h-16 rounded-xl object-cover" />
              <div>
                <span className="font-heading font-semibold text-[#0B1533]">{s.name}</span>
                <p className="font-body text-xs text-[#5A607A]">{s.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {grouped.map((group) => (
          <div key={group.canton} className="mb-10">
            <h2 className="font-heading font-semibold text-lg text-[#0B1533] mb-4 flex items-center gap-2"><MapPin size={18} className="text-[#2B4BFF]" /> {group.canton}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.cities.map((c) => (
                <Link key={c.slug} to={c.path} data-testid={`city-card-${c.slug}`} className="group flex flex-col rounded-2xl border border-[#E9E5DE] bg-white overflow-hidden hover:border-[#2B4BFF] hover:elev-soft transition-all">
                  <div className="h-32 overflow-hidden">
                    <img src={cityImage(c)} alt={`Flughafentransfer ${c.name}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                  </div>
                  <div className="flex items-center justify-between px-5 py-4">
                    <div>
                      <span className="font-heading font-semibold text-[#0B1533]">Flughafentransfer {c.name}</span>
                      <p className="font-body text-xs text-[#5A607A] flex items-center gap-1 mt-0.5"><Clock size={12} /> ~{c.min} min · {c.km} km</p>
                    </div>
                    <ArrowRight size={16} className="text-[#2B4BFF] group-hover:translate-x-1 transition-transform shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Ratgeber;
